import jsPDF from 'jspdf'
import {getAuth} from '../../auth/core/AuthHelpers'
type CsvValue = string | number | boolean | null | undefined

const sanitizeFileName = (value: string) =>
  value
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')

const buildFileName = (baseName: string, extension: string) => {
  const safeBase = sanitizeFileName(baseName || 'export')
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  return `${safeBase}_${stamp}.${extension}`
}

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const toPrettyLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '-'
  if (typeof value === 'string') return value.trim() || '-'
  if (value instanceof Date) return value.toISOString()
  return String(value)
}

const flattenObjectRows = (
  value: unknown,
  section = ''
): Array<{label: string; value: string}> => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [{label: section || 'Items', value: '-'}]
    }
    const primitiveArray = value.every((item) => !isPlainObject(item) && !Array.isArray(item))
    if (primitiveArray) {
      return [{label: section || 'Items', value: value.map((item) => toDisplayValue(item)).join(', ')}]
    }
    return value.flatMap((item, idx) =>
      flattenObjectRows(item, section ? `${section} #${idx + 1}` : `Item #${idx + 1}`)
    )
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value)
    if (entries.length === 0) return [{label: section || 'Data', value: '-'}]
    return entries.flatMap(([key, val]) => {
      const nextLabel = section ? `${section} > ${toPrettyLabel(key)}` : toPrettyLabel(key)
      return flattenObjectRows(val, nextLabel)
    })
  }

  return [{label: section || 'Value', value: toDisplayValue(value)}]
}

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

const waitForExportReady = async (element: HTMLElement, timeoutMs: number) => {
  const startedAt = Date.now()
  while (Date.now() - startedAt < timeoutMs) {
    const rootReadyFlag = element.getAttribute('data-export-ready')
    const taggedNodes = Array.from(
      element.querySelectorAll<HTMLElement>('[data-export-ready]')
    )
    const hasPendingNode =
      rootReadyFlag === 'false' ||
      taggedNodes.some((node) => node.getAttribute('data-export-ready') === 'false')
    if (!hasPendingNode) return
    await sleep(80)
  }
}

const waitForImages = async (element: HTMLElement, timeoutMs: number) => {
  const startedAt = Date.now()
  while (Date.now() - startedAt < timeoutMs) {
    const images = Array.from(element.querySelectorAll('img'))
    const pending = images.filter((img) => !img.complete || img.naturalWidth === 0)
    if (pending.length === 0) return
    await sleep(100)
  }
}

const toDataUrl = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })

const fetchImageDataUrlForPdf = async (rawSrc: string): Promise<string | null> => {
  const src = String(rawSrc || '').trim()
  if (!src) return null
  if (src.startsWith('data:')) return src

  const token = getAuth()?.auth?.jwt
  const attempts: RequestInit[] = token
    ? [
        {headers: {Authorization: `Bearer ${token}`}, credentials: 'include'},
        {headers: {Authorization: `Bearer ${token}`}},
        {credentials: 'include'},
        {},
      ]
    : [{credentials: 'include'}, {}]

  for (const options of attempts) {
    try {
      const response = await fetch(src, options)
      if (!response.ok) continue
      const blob = await response.blob()
      return await toDataUrl(blob)
    } catch {
      // continue attempts
    }
  }
  return null
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = src
  })

const buildWatermarkLayerDataUrl = async (rawSrc: string): Promise<string | null> => {
  const source = (await fetchImageDataUrlForPdf(rawSrc)) || rawSrc
  try {
    const image = await loadImage(source)
    const width = image.naturalWidth || image.width
    const height = image.naturalHeight || image.height
    if (!width || !height) return null

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.clearRect(0, 0, width, height)
    ctx.globalAlpha = 0.12
    ctx.drawImage(image, 0, 0, width, height)
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

const getWatermarkSource = (element: HTMLElement): string | null => {
  const preferred = element.querySelector<HTMLImageElement>('[data-pdf-watermark-source="true"]')
  if (preferred?.src) return preferred.src
  const fallback = element.querySelector<HTMLImageElement>('.procurement-doc-watermark-image')
  return fallback?.src || null
}

export const applyPerPageWatermark = async (pdf: any, element: HTMLElement) => {
  const watermarkRaw = getWatermarkSource(element)
  if (!watermarkRaw) return

  const watermarkLayer = await buildWatermarkLayerDataUrl(watermarkRaw)
  if (!watermarkLayer) return

  const watermarkImg = await loadImage(watermarkLayer).catch(() => null)
  if (!watermarkImg) return

  const imageWidth = watermarkImg.naturalWidth || watermarkImg.width
  const imageHeight = watermarkImg.naturalHeight || watermarkImg.height
  if (!imageWidth || !imageHeight) return

  const pageCount = pdf.internal.getNumberOfPages()
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page)
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    let targetWidth = pageWidth * 0.56
    let targetHeight = (targetWidth * imageHeight) / imageWidth

    const maxHeight = pageHeight * 0.52
    if (targetHeight > maxHeight) {
      targetHeight = maxHeight
      targetWidth = (targetHeight * imageWidth) / imageHeight
    }

    const offsetY = pageHeight * 0.08 // Shift watermark up as requested by client
    const x = (pageWidth - targetWidth) / 2
    const y = Math.max((pageHeight - targetHeight) / 2 - offsetY, 8)
    pdf.addImage(watermarkLayer, 'PNG', x, y, targetWidth, targetHeight, `wm_${page}`, 'FAST')
  }
}

export const downloadPdfFile = (data: unknown, baseName: string) => {
  const doc = new jsPDF({orientation: 'portrait', unit: 'pt', format: 'a4'})
  const rows = flattenObjectRows(data).filter((row) => row.label && row.value)
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 36
  const marginTop = 40
  const lineGap = 14
  const labelWidth = 210
  const valueWidth = pageWidth - marginX * 2 - labelWidth - 8

  let y = marginTop
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text(toPrettyLabel(baseName || 'Procurement Record'), marginX, y)

  y += 22
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(`Generated: ${new Date().toLocaleString()}`, marginX, y)
  y += 18

  rows.forEach(({label, value}) => {
    const labelLines = doc.splitTextToSize(label, labelWidth)
    const valueLines = doc.splitTextToSize(value, valueWidth)
    const rowHeight = Math.max(labelLines.length, valueLines.length) * lineGap + 2

    if (y + rowHeight > pageHeight - 28) {
      doc.addPage()
      y = marginTop
    }

    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.text(labelLines, marginX, y)
    doc.setFont('helvetica', 'normal')
    doc.text(valueLines, marginX + labelWidth + 8, y)
    y += rowHeight
  })

  doc.save(buildFileName(baseName, 'pdf'))
}

export const downloadElementAsPdf = async (
  source: string | HTMLElement,
  baseName: string,
  options?: {timeoutMs?: number; settleMs?: number}
) => {
  const timeoutMs = options?.timeoutMs ?? 12000
  const settleMs = options?.settleMs ?? 350
  const startedAt = Date.now()
  let element: HTMLElement | null = null

  if (typeof source === 'string') {
    while (!element && Date.now() - startedAt < timeoutMs) {
      element = document.querySelector(source) as HTMLElement | null
      if (!element) {
        await sleep(100)
      }
    }
  } else {
    element = source
  }

  if (!element) {
    throw new Error('Unable to find export view container')
  }

  const fileName = buildFileName(baseName, 'pdf')

  // Clone element into a temporary top-level container to prevent z-index obscuring by page background
  const clone = element.cloneNode(true) as HTMLElement
  clone.classList.add('procurement-export-mode')
  clone.style.position = 'static'
  clone.style.transform = 'none'
  clone.style.visibility = 'visible'
  clone.style.opacity = '1'
  clone.style.display = 'block'

  const tempWrapper = document.createElement('div')
  tempWrapper.style.position = 'fixed'
  tempWrapper.style.top = '0px'
  tempWrapper.style.left = '-10000px'
  tempWrapper.style.width = '1400px'
  tempWrapper.style.zIndex = '999999'
  tempWrapper.style.background = '#ffffff'
  tempWrapper.style.visibility = 'visible'
  tempWrapper.style.opacity = '1'
  tempWrapper.appendChild(clone)
  document.body.appendChild(tempWrapper)

  try {
    await waitForExportReady(tempWrapper, timeoutMs)
    await sleep(settleMs)
    await waitForImages(tempWrapper, timeoutMs)

    const html2pdf = (await import('html2pdf.js')).default as any
    const exportWidth = Math.max(clone.scrollWidth, clone.clientWidth, 1200)
    const exportHeight = Math.max(clone.scrollHeight, clone.clientHeight, 800)
    const isLandscape = exportWidth > 950

    const worker = html2pdf()
      .set({
        margin: [6, 6, 6, 6],
        filename: fileName,
        image: {type: 'jpeg', quality: 0.98},
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          imageTimeout: 20000,
          windowWidth: exportWidth,
          windowHeight: exportHeight,
          logging: false,
        },
        jsPDF: {unit: 'mm', format: 'a4', orientation: isLandscape ? 'landscape' : 'portrait'},
        pagebreak: {mode: ['css', 'legacy'], avoid: ['tr', '.procurement-avoid-break']},
      })
      .from(clone)
      .toPdf()

    const pdf = await worker.get('pdf')
    await applyPerPageWatermark(pdf, clone)
    pdf.save(fileName)
  } finally {
    if (tempWrapper.parentNode) {
      tempWrapper.parentNode.removeChild(tempWrapper)
    }
  }
}

// Backward-compatible alias: legacy callers still named this as JSON download.
export const downloadJsonFile = (data: unknown, baseName: string) => {
  downloadPdfFile(data, baseName)
}

const encodeCsvCell = (value: CsvValue) => {
  if (value === null || value === undefined) return ''
  const text = String(value)
  if (text.includes('"') || text.includes(',') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

export const downloadCsvFile = (
  headers: string[],
  rows: CsvValue[][],
  baseName: string
) => {
  const headerRow = headers.map(encodeCsvCell).join(',')
  const bodyRows = rows.map((row) => row.map(encodeCsvCell).join(','))
  const csv = [headerRow, ...bodyRows].join('\n')
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
  triggerBlobDownload(blob, buildFileName(baseName, 'csv'))
}
